/* =========================================================
   ESSE WERKS — CART JS
   AJAX cart against Shopify's /cart/*.js endpoints.
   - window.ewCart.add({id, quantity, properties}) → adds + opens drawer
   - window.ewCart.changeByLine(key, quantity)     → updates quantity
   - window.ewCart.removeByLine(key)               → removes a line
   - window.ewCart.open() / close() / toggle()
   - Mobile drawer + cart drawer share esc-to-close + click-outside.

   No framework, no transpile. Public API is `window.ewCart`.
   ========================================================= */
(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const ROUTES = (window.routes || {});
  const ADD_URL = ROUTES.cart_add_url     || '/cart/add.js';
  const CHANGE_URL = ROUTES.cart_change_url || '/cart/change.js';
  const CART_URL = ROUTES.cart_url || '/cart';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    }, opts || {})).then((r) => {
      if (!r.ok) {
        return r.json().then((body) => { throw body; }).catch(() => { throw new Error('HTTP ' + r.status); });
      }
      return r.json();
    });
  }

  function refreshDrawer() {
    /* Re-fetch this section's HTML via Section Rendering API and swap
       in the new contents. Keeps drawer state (open/closed) intact. */
    return fetch(`${CART_URL}?sections=${SECTION_ID}`, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((sections) => {
        const html = sections[SECTION_ID];
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const newDrawer = tmp.querySelector('[data-ew-cart-drawer]');
        const oldDrawer = $('[data-ew-cart-drawer]');
        if (newDrawer && oldDrawer) {
          /* Preserve aria-hidden + inert so the open/closed state survives */
          var hidden = oldDrawer.getAttribute('aria-hidden') || 'true';
          newDrawer.setAttribute('aria-hidden', hidden);
          if (hidden === 'true') newDrawer.setAttribute('inert', '');
          else newDrawer.removeAttribute('inert');
          oldDrawer.replaceWith(newDrawer);
          bindDrawerEvents();
        }
      });
  }

  function refreshBadge() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((cart) => {
        const badge = $('[data-ew-cart-badge]');
        if (!badge) return cart;
        if (cart.item_count > 0) {
          badge.textContent = String(cart.item_count);
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
        return cart;
      });
  }

  /* --- Drawer open/close --- */
  /* Element that had focus before the drawer opened, so we can restore
     focus to it on close (mirrors ew-garage-dialog.js `lastTrigger`). */
  let lastCartTrigger = null;

  /* Visible, tabbable elements inside the drawer, in DOM order.
     getClientRects() filters display:none / hidden-subtree nodes and
     works for the fixed-position panel where offsetParent is null.
     Mirrors ew-garage-dialog.js getFocusable(). */
  function getCartFocusable(d) {
    return $$('a[href],area[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', d)
      .filter((el) => el.getClientRects().length > 0);
  }

  /* Tab focus-trap, active only while the drawer is open. The backdrop is
     not inert, so without this, keyboard focus could tab out behind the
     modal. Wrap first<->last. Escape-close lives in the global keydown
     handler (bindGlobal). Mirrors ew-garage-dialog.js onKeydown Tab branch. */
  function onCartKeydown(e) {
    if (e.key !== 'Tab') return;
    const d = $('[data-ew-cart-drawer]');
    if (!d) return;
    const f = getCartFocusable(d);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    const active = document.activeElement;
    if (!d.contains(active)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }

  function openDrawer() {
    const d = $('[data-ew-cart-drawer]');
    if (!d) return;
    /* Capture the trigger BEFORE we move focus so close can restore it. */
    lastCartTrigger = document.activeElement;
    d.setAttribute('aria-hidden', 'false');
    d.removeAttribute('inert');
    document.body.classList.add('ew-no-scroll');
    /* Move focus into the drawer: close button first, else first focusable. */
    const focusTarget = d.querySelector('[data-ew-cart-close]') || getCartFocusable(d)[0];
    if (focusTarget) focusTarget.focus();
    document.addEventListener('keydown', onCartKeydown);
  }
  function closeDrawer() {
    const d = $('[data-ew-cart-drawer]');
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    d.setAttribute('inert', '');
    document.body.classList.remove('ew-no-scroll');
    document.removeEventListener('keydown', onCartKeydown);
    /* Restore focus to the element that opened the drawer. */
    if (lastCartTrigger && typeof lastCartTrigger.focus === 'function') lastCartTrigger.focus();
    lastCartTrigger = null;
  }
  function toggleDrawer() {
    const d = $('[data-ew-cart-drawer]');
    if (!d) return;
    if (d.getAttribute('aria-hidden') === 'false') closeDrawer();
    else openDrawer();
  }

  /* --- Mobile nav drawer (separate from cart drawer) --- */
  function openMobile() {
    const d = $('[data-ew-mobile-drawer]');
    if (!d) return;
    d.setAttribute('aria-hidden', 'false');
    d.removeAttribute('inert');
    document.body.classList.add('ew-no-scroll');
  }
  function closeMobile() {
    const d = $('[data-ew-mobile-drawer]');
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    d.setAttribute('inert', '');
    document.body.classList.remove('ew-no-scroll');
  }

  /* --- Cart mutations --- */
  function add(opts) {
    /* opts: { id (variantId, required), quantity, properties } */
    if (!opts || !opts.id) return Promise.reject(new Error('ewCart.add: id required'));
    const body = {
      items: [{ id: opts.id, quantity: opts.quantity || 1 }],
    };
    if (opts.properties) body.items[0].properties = opts.properties;
    return fetchJSON(ADD_URL, { method: 'POST', body: JSON.stringify(body) })
      .then(() => Promise.all([refreshDrawer(), refreshBadge()]))
      .then(() => { openDrawer(); });
  }

  function changeByLine(lineKey, quantity) {
    return fetchJSON(CHANGE_URL, {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    }).then(() => Promise.all([refreshDrawer(), refreshBadge()]));
  }

  function removeByLine(lineKey) {
    return changeByLine(lineKey, 0);
  }

  /* --- Event delegation inside the drawer --- */
  function bindDrawerEvents() {
    const drawer = $('[data-ew-cart-drawer]');
    if (!drawer) return;

    /* Close button */
    drawer.querySelectorAll('[data-ew-cart-close]').forEach((btn) => {
      btn.addEventListener('click', closeDrawer);
    });

    /* Click outside the panel = close */
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) closeDrawer();
    });

    /* Per-line qty / remove */
    drawer.querySelectorAll('[data-ew-cart-line]').forEach((line) => {
      const key = line.getAttribute('data-line-key');
      const qty = line.querySelector('[data-ew-cart-qty-value]');
      const dec = line.querySelector('[data-ew-cart-qty-decrease]');
      const inc = line.querySelector('[data-ew-cart-qty-increase]');
      const rm  = line.querySelector('[data-ew-cart-remove]');
      const current = () => parseInt(qty && qty.textContent, 10) || 0;
      if (dec) dec.addEventListener('click', () => changeByLine(key, Math.max(0, current() - 1)));
      if (inc) inc.addEventListener('click', () => changeByLine(key, current() + 1));
      if (rm)  rm.addEventListener('click',  () => removeByLine(key));
    });
  }

  /* --- Build-group bulk operations (Edit / Remove) --- */
  function parseLineKeys(attr) {
    return (attr || '').split(',').map((s) => s.trim()).filter(Boolean);
  }

  function removeLineKeysBulk(keys) {
    /* /cart/update.js supports bulk updates via `updates` keyed by
       line key. quantity 0 removes the line. */
    if (!keys.length) return Promise.resolve();
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ updates: updates }),
    });
  }

  function findConfigStateForBuild(cart, buildId) {
    if (!cart || !cart.items) return null;
    for (const item of cart.items) {
      const props = item.properties || {};
      if (props._build_id === buildId && props._config_state) {
        try { return JSON.parse(props._config_state); } catch (_) { /* fall through */ }
      }
    }
    return null;
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
  }

  function handleRemoveBuild(btn) {
    const keys = parseLineKeys(btn.getAttribute('data-line-keys'));
    btn.disabled = true;
    removeLineKeysBulk(keys)
      .then(() => Promise.all([refreshDrawer(), refreshBadge()]))
      .catch(() => { btn.disabled = false; });
  }

  function handleEditBuild(btn) {
    const buildId = btn.getAttribute('data-build-id');
    const keys = parseLineKeys(btn.getAttribute('data-line-keys'));
    btn.disabled = true;
    /* 1. Fetch the cart to read _config_state from the stage line. */
    fetchCart()
      .then((cart) => {
        const state = findConfigStateForBuild(cart, buildId);
        if (!state) throw new Error('Could not read build state from cart');
        if (!window.ewConfigurator || !window.ewConfigurator.rehydrate) {
          throw new Error('Configurator not loaded');
        }
        window.ewConfigurator.rehydrate(state);
        return removeLineKeysBulk(keys);
      })
      .then(() => Promise.all([refreshDrawer(), refreshBadge()]))
      .then(() => { window.location.href = '/pages/configure-review'; })
      .catch((err) => {
        btn.disabled = false;
        alert((err && err.message) || 'Could not edit build. Try again.');
      });
  }

  /* --- Cart PAGE "−" quantity button (progressive enhancement) --- */
  /* The page table's "−" is a submit button sharing name updates[<key>]
     with the qty input that follows it in tree order. The submitter's
     form-data entry serializes at its tree position and Shopify keeps
     the LAST duplicate, so the input's unchanged value overrides the
     button's decremented one — a native click is a silent no-op. With
     JS we write the decremented value into the input instead, then
     submit the form with NO submitter so only the inputs serialize.
     DOM order is untouched, and bindGlobal's guard filters out the
     synthetic click that Enter-in-input implicit submission fires at
     this button, so typing a qty + Enter keeps working exactly as
     before. At quantity 1 the "−" posts 0, which removes the line —
     same semantics as Remove and the input's min="0". */
  function handlePageQtyMinus(btn, form) {
    const input = $$('input[type="number"]', btn.closest('tr') || form)
      .find((i) => i.name === btn.name);
    if (!input) return;
    let next = parseInt(input.value, 10) - 1;
    /* Input cleared by the user → fall back to the button's
       server-rendered value, which is already quantity - 1. */
    if (isNaN(next)) next = parseInt(btn.value, 10) || 0;
    input.value = String(Math.max(0, next));
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  /* --- One-time global wiring --- */
  function bindGlobal() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-ew-cart-open]');
      if (opener) { e.preventDefault(); openDrawer(); return; }

      const mopen  = e.target.closest('[data-ew-mobile-open]');
      if (mopen)   { e.preventDefault(); openMobile(); return; }
      const mclose = e.target.closest('[data-ew-mobile-close]');
      if (mclose)  { e.preventDefault(); closeMobile(); return; }
      const mdrawer = e.target.closest('[data-ew-mobile-drawer]');
      if (mdrawer && e.target === mdrawer) { closeMobile(); return; }

      const removeBuild = e.target.closest('[data-ew-remove-build]');
      if (removeBuild) { e.preventDefault(); handleRemoveBuild(removeBuild); return; }
      const editBuild  = e.target.closest('[data-ew-edit-build]');
      if (editBuild)   { e.preventDefault(); handleEditBuild(editBuild);   return; }

      /* Cart PAGE only. Two guards BEFORE preventing default:
         1. the button must belong to the page form (#ew-cart-form);
         2. the click must be a genuine activation of the button —
            e.detail > 0 (pointer) or focus on the button (keyboard).
            Enter inside a qty input fires a SYNTHETIC click at the
            form's default button — this very "−" on row 1 — with
            detail 0 and focus still in the input (implicit
            submission). That click must fall through to the native
            submit so the typed value posts, instead of wrongly
            decrementing row 1. Any unmatched case = native submit =
            today's behavior, never a new failure mode. */
      const pageMinus = e.target.closest('[data-ew-page-qty-minus]');
      if (pageMinus && pageMinus.form && pageMinus.form.id === 'ew-cart-form' &&
          (e.detail > 0 || document.activeElement === pageMinus)) {
        e.preventDefault();
        handlePageQtyMinus(pageMinus, pageMinus.form);
        return;
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { closeDrawer(); closeMobile(); }
    });
  }

  /* --- Public API --- */
  window.ewCart = {
    add: add,
    changeByLine: changeByLine,
    removeByLine: removeByLine,
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    refresh: () => Promise.all([refreshDrawer(), refreshBadge()]),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { bindGlobal(); bindDrawerEvents(); });
  } else {
    bindGlobal();
    bindDrawerEvents();
  }
})();
